import { type Component, createSignal, createResource, Show, For, createEffect } from 'solid-js'
import { useNavigate, useParams } from '@solidjs/router'
import { useKeywordFlagging } from '../../../shared/providers/coris/coris-keyword-flagging.provider'
import { useUser } from '../../../shared/providers/user-provider'
import { AppRoutes } from '../../../shared/app-routes'
import { downloadCSV } from '../../../shared/helpers/download-csv'
import { Icon } from '@iconify-icon/solid'

const KeywordFlaggingForm: Component = () => {
  const params = useParams<{ id?: string }>()
  const navigate = useNavigate()
  const { getKeywordList, saveKeywordList } = useKeywordFlagging()
  const { organizationSlug } = useUser()

  const isEditMode = () => !!params.id
  const [name, setName] = createSignal('')
  const [keywords, setKeywords] = createSignal<string[]>([])
  const [autoModeration, setAutoModeration] = createSignal(false)
  const [stagedTags, setStagedTags] = createSignal<string[]>([])
  const [tagInput, setTagInput] = createSignal('')
  const [keywordSearch, setKeywordSearch] = createSignal('')
  const [isSaving, setIsSaving] = createSignal(false)

  // Load existing data for edit mode
  const [existingData] = createResource(
    () => {
      const slug = organizationSlug()
      if (!slug || !params.id) return null
      return { id: params.id, slug }
    },
    async (source) => {
      return await getKeywordList.execute(source.id)
    }
  )

  // Populate form when data loads
  createEffect(() => {
    const data = existingData()
    if (data) {
      setName(data.name)
      setKeywords([...data.keywords])
      setAutoModeration(data.autoModeration)
    }
  })

  const filteredKeywords = () => {
    const term = keywordSearch().toLowerCase().trim()
    if (!term) return keywords()
    return keywords().filter((k) => k.toLowerCase().includes(term))
  }

  const commitTagInput = (): void => {
    const input = tagInput().trim()
    if (!input) return

    const newTags = input
      .split(',')
      .map((w) => w.trim())
      .filter((w) => w.length > 0)
      .filter((w) => !stagedTags().includes(w))

    if (newTags.length > 0) {
      setStagedTags((prev) => [...prev, ...newTags])
    }
    setTagInput('')
  }

  const handleTagInputKeyDown = (e: KeyboardEvent): void => {
    if (e.key === ',') {
      e.preventDefault()
      commitTagInput()
    }
    if (e.key === 'Backspace' && tagInput() === '' && stagedTags().length > 0) {
      setStagedTags((prev) => prev.slice(0, -1))
    }
  }

  const handleAddToList = (): void => {
    commitTagInput()
    const toAdd = stagedTags().filter((t) => !keywords().includes(t))
    if (toAdd.length > 0) {
      setKeywords((prev) => [...prev, ...toAdd])
    }
    setStagedTags([])
  }

  let tagInputRef: HTMLInputElement | undefined

  const handleClearAll = (): void => {
    setKeywords([])
    setKeywordSearch('')
  }

  const handleCsvExport = (): void => {
    const csv = keywords().join('\n')
    downloadCSV(csv, `${name() || 'keywords'}.csv`)
  }

  const handleSave = async (): Promise<void> => {
    if (!name().trim()) return
    setIsSaving(true)
    try {
      await saveKeywordList.execute({
        id: params.id,
        name: name().trim(),
        keywords: keywords(),
        autoModeration: autoModeration()
      })
      navigate(AppRoutes.KeywordFlagging())
    } finally {
      setIsSaving(false)
    }
  }

  const handleCancel = (): void => {
    navigate(AppRoutes.KeywordFlagging())
  }

  return (
    <div class="flex flex-col h-full">
      {/* Form content */}
      <div class="flex-1 px-6 py-8 flex flex-col items-center">
        <div class="w-full max-w-[600px] flex flex-col gap-8 flex-1">
          {/* Card */}
          <div class="flex-1 p-6 bg-white rounded-xl shadow-sm flex flex-col gap-4">
            <div class="flex-1 flex flex-col gap-4">
              {/* Filter Name */}
              <div class="flex flex-col gap-2">
                <label class="text-sm font-medium text-gray-900">Filter Name</label>
                <input
                  type="text"
                  placeholder="Enter filter name"
                  value={name()}
                  onInput={(e) => setName(e.currentTarget.value)}
                  class="w-full px-3 py-2 bg-white rounded-md border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              {/* Tag input + Add button */}
              <div class="flex items-start gap-2">
                <div
                  class="flex-1 flex flex-wrap items-center gap-1.5 p-1 bg-white rounded-md border border-gray-300 focus-within:ring-2 focus-within:ring-primary-500 focus-within:border-primary-500 cursor-text max-h-[120px] overflow-y-auto"
                  onClick={() => tagInputRef?.focus()}
                >
                  <For each={stagedTags()}>
                    {(tag) => (
                      <span class="inline-flex items-center gap-1 px-2 py-0.5 bg-primary-50 text-primary-700 rounded text-sm font-medium">
                        {tag}
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation()
                            setStagedTags((prev) => prev.filter((t) => t !== tag))
                          }}
                          class="text-primary-400 hover:text-primary-700"
                        >
                          <Icon icon="heroicons:x-mark" class="text-[0.75rem]" />
                        </button>
                      </span>
                    )}
                  </For>
                  <input
                    ref={tagInputRef}
                    type="text"
                    placeholder={stagedTags().length === 0 ? 'Type a keyword and press comma...' : ''}
                    value={tagInput()}
                    onInput={(e) => setTagInput(e.currentTarget.value)}
                    onKeyDown={handleTagInputKeyDown}
                    class="flex-1 min-w-[120px] py-1 border-none focus:ring-0 outline-none text-sm placeholder:text-gray-400 text-gray-900 bg-transparent"
                  />
                </div>
                <button
                  type="button"
                  onClick={handleAddToList}
                  disabled={stagedTags().length === 0 && tagInput().trim() === ''}
                  class="px-6 py-2 bg-primary-50 text-primary-700 rounded-md shadow-sm text-sm font-semibold hover:bg-primary-100 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
                >
                  <Icon icon="heroicons:plus" class="text-[1rem]" />
                  Add
                </button>
              </div>

              {/* Keywords list with search */}
              <div class="flex-1 flex flex-col border border-gray-300 rounded-md">
                {/* Search header - InputGroup style */}
                <div class="flex items-center gap-2 px-2 py-1 rounded-t-md border-b border-gray-300 text-gray-400 focus-within:text-primary-600 focus-within:ring-2 focus-within:ring-primary-500 focus-within:border-primary-500">
                  <Icon icon="heroicons:magnifying-glass" class="text-[1rem]" />
                  <input
                    type="text"
                    placeholder="Search keywords..."
                    value={keywordSearch()}
                    onInput={(e) => setKeywordSearch(e.currentTarget.value)}
                    class="flex-1 p-1 border-none focus:ring-0 outline-none text-sm placeholder:text-gray-400 text-gray-900"
                  />
                </div>

                {/* Keyword rows */}
                <div class="flex-1 overflow-y-auto max-h-80">
                  <Show
                    when={filteredKeywords().length > 0}
                    fallback={
                      <div class="px-4 py-8 text-center text-sm text-gray-400">
                        {keywords().length === 0 ? 'No keywords added yet.' : 'No matching keywords.'}
                      </div>
                    }
                  >
                    <For each={filteredKeywords()}>
                      {(keyword) => (
                        <div class="px-4 py-2.5 border-b border-gray-200 flex items-center justify-between group">
                          <span class="text-sm font-medium text-gray-700">{keyword}</span>
                          <button
                            type="button"
                            onClick={() => setKeywords((prev) => prev.filter((k) => k !== keyword))}
                            class="opacity-0 group-hover:opacity-100 w-7 h-7 rounded-md flex items-center justify-center text-gray-400 border border-transparent hover:border-gray-200 hover:shadow-sm transition-all"
                            title="Remove keyword"
                          >
                            <Icon icon="heroicons:x-mark" class="text-[1rem]" />
                          </button>
                        </div>
                      )}
                    </For>
                  </Show>
                </div>
              </div>

              {/* Auto-moderation checkbox */}
              <label class="inline-flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={autoModeration()}
                  onChange={(e) => setAutoModeration(e.currentTarget.checked)}
                  class="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <span class="text-sm font-normal text-gray-900">Enable auto-moderation</span>
              </label>

              {/* Clear All / CSV Export */}
              <div class="flex items-center gap-4">
                <button
                  type="button"
                  onClick={handleClearAll}
                  class="px-6 py-1.5 bg-white rounded-md shadow-sm border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Clear All
                </button>
                <Show when={isEditMode()}>
                  <button
                    type="button"
                    onClick={handleCsvExport}
                    class="px-6 py-1.5 bg-white rounded-md shadow-sm border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-50 flex items-center gap-1.5"
                  >
                    <Icon icon="heroicons:arrow-down-tray" class="text-[1rem]" />
                    CSV Export
                  </button>
                </Show>
              </div>
            </div>
          </div>

          {/* Bottom action buttons */}
          <div class="flex justify-end gap-4">
            <button
              type="button"
              onClick={handleCancel}
              class="min-w-24 px-3.5 py-2.5 bg-white rounded-md shadow-sm border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={isSaving() || !name().trim()}
              class="min-w-24 px-3.5 py-2.5 bg-primary-800 rounded-md shadow-sm text-sm font-semibold text-white hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSaving() ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default KeywordFlaggingForm
